import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, X, Plus, Info, User as UserIcon, LogOut, FileText, Download, ArrowLeft, Phone, Linkedin, Pencil, Briefcase, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { getProfile, updateProfile, uploadResume, deleteResume, downloadResume, searchOrganizations, searchSchools } from '../api/insights';
import MonthYearPicker from './MonthYearPicker';
const emptyExperience = () => ({
  company: '',
  role: '',
  start_date: '',
  end_date: '',
  currently_working: false,
});
const emptyEducation = () => ({
  college: '',
  degree: '',
  start_date: '',
  end_date: '',
  currently_studying: false,
});
const MAX_RESUME_SIZE_BYTES = 300 * 1024;
const MIN_MONTH = '1960-01';
const CURRENT_YEAR_END_MONTH = `${new Date().getFullYear()}-12`;
const INSIGHTS_CACHE_PREFIX = 'fairhire_insights_cache:';
const isValidMonth = (value) => /^\d{4}-\d{2}$/.test(value || '');
const clampToCurrentYearEnd = (value) => {
  if (!isValidMonth(value)) return '';
  return value > CURRENT_YEAR_END_MONTH ? CURRENT_YEAR_END_MONTH : value;
};
const validateProfileDates = (items, label, currentField) => {
  for (let i = 0; i < items.length; i += 1) {
    const row = items[i] || {};
    const start = row.start_date || '';
    const end = row.end_date || '';
    const isCurrent = !!row[currentField];
    if (start && (!isValidMonth(start) || start < MIN_MONTH || start > CURRENT_YEAR_END_MONTH)) {
      return `${label} ${i + 1}: start date must be between ${MIN_MONTH} and ${CURRENT_YEAR_END_MONTH}.`;
    }
    if (end && (!isValidMonth(end) || end < MIN_MONTH || end > CURRENT_YEAR_END_MONTH)) {
      return `${label} ${i + 1}: end date cannot be after ${CURRENT_YEAR_END_MONTH}.`;
    }
    if (!isCurrent && start && end && end < start) {
      return `${label} ${i + 1}: end date cannot be before start date.`;
    }
  }
  return null;
};
const toMonthValue = (value) => {
  const s = (value || '').toString().trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 7);
  return '';
};
const formatMonthYear = (monthStr) => {
  if (!monthStr) return '';
  const m = toMonthValue(monthStr);
  if (!m) return '';
  const [year, month] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
};
const clearInsightsCache = () => {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(INSIGHTS_CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch (_) {
    // ignore storage errors
  }
};
/* ── Helper: check if an item is completely blank ── */
const isBlankExperience = (item) => !item?.company && !item?.role && !item?.start_date && !item?.end_date;
const isBlankEducation = (item) => !item?.college && !item?.degree && !item?.start_date && !item?.end_date;
const normalizeComparableProfile = (profile) => {
  const safe = profile || {};

  const work_experiences = (Array.isArray(safe.work_experiences) ? safe.work_experiences : [])
    .map((exp) => ({
      company: exp.company || '',
      role: exp.role || '',
      start_date: toMonthValue(exp.start_date),
      end_date: exp.currently_working ? '' : toMonthValue(exp.end_date),
    }))
    .sort((a, b) => {
      const scoreA = a.end_date || a.start_date || '';
      const scoreB = b.end_date || b.start_date || '';
      return scoreB.localeCompare(scoreA) || a.company.localeCompare(b.company);
    });
  const educations = (Array.isArray(safe.educations) ? safe.educations : [])
    .map((edu) => ({
      college: edu.college || '',
      degree: edu.degree || '',
      start_date: toMonthValue(edu.start_date),
      end_date: edu.currently_studying ? '' : toMonthValue(edu.end_date),
    }))
    .sort((a, b) => {
      const scoreA = a.end_date || a.start_date || '';
      const scoreB = b.end_date || b.start_date || '';
      return scoreB.localeCompare(scoreA) || a.college.localeCompare(b.college);
    });
  const skills = (Array.isArray(safe.skills) ? safe.skills : [])
    .map((s) => (typeof s === 'string' ? s : s?.skill) || '')
    .filter(Boolean)
    .sort();
  const open_to_roles = (Array.isArray(safe.open_to_roles) ? safe.open_to_roles : [])
    .map((r) => (typeof r === 'string' ? r : r?.role) || '')
    .filter(Boolean)
    .sort();
  return {
    name: safe.name || '',
    phone_number: safe.phone_number || '',
    linkedin_url: safe.linkedin_url || '',
    bio: safe.bio || '',
    work_experiences,
    educations,
    skills,
    open_to_roles,
  };
};
/* ── Inline Editable Field ── */
const InlineEditField = ({ value, onChange, placeholder, icon: Icon, iconColor, style = {}, editingBorderColor, boxedEdit, externalEditing, onEditStart }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);
  const isEditing = boxedEdit ? externalEditing : editing;
  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);
  const commit = () => {
    if (boxedEdit && onEditStart) onEditStart(false);
    else setEditing(false);
    if (draft !== value) onChange(draft);
  };
  const startEdit = () => {
    if (boxedEdit && onEditStart) onEditStart(true);
    else setEditing(true);
  };
  const activeBorder = editingBorderColor || '#2563eb';
  if (isEditing) {
    return (
      <div className="profile-inline-edit-wrapper" style={boxedEdit
        ? { ...style, background: 'rgba(37,99,235,0.04)', border: `1.5px solid ${activeBorder}`, borderRadius: '10px', padding: '8px 14px' }
        : { ...style, borderBottom: `2px solid ${activeBorder}`, paddingBottom: '2px' }
      }>
        {Icon && <Icon className="profile-inline-icon" style={{ color: iconColor || '#888' }} />}
        <div className="profile-inline-edit-content">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); commit(); } }}
            className="profile-inline-input"
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  }
  return (
    <div
      className="profile-inline-edit-wrapper profile-inline-clickable"
      style={{ ...style, borderBottom: `1px dashed ${value ? 'transparent' : (editingBorderColor || 'rgba(148,163,184,0.45)')}`, paddingBottom: '3px' }}
      onClick={startEdit}
      title="Click to edit"
    >
      {Icon && <Icon className="profile-inline-icon" style={{ color: iconColor || '#888' }} />}
      <div className="profile-inline-edit-content">
        <span className={`profile-inline-value ${!value ? 'profile-inline-placeholder' : ''}`}>
          {value || placeholder}
        </span>
      </div>
    </div>
  );
};
/* ── Name Edit Field with Boxed Mode ── */
const NameEditField = ({ name, setName, txtBody, txtSecondary, accentColor }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <InlineEditField
        value={name}
        onChange={setName}
        placeholder="Your name"
        editingBorderColor={accentColor}
        boxedEdit={true}
        externalEditing={editing}
        onEditStart={setEditing}
        style={{ fontSize: '1.35rem', fontWeight: 700, color: txtBody }}
      />
      {!editing && (
        <Pencil
          className="w-3.5 h-3.5 flex-shrink-0 opacity-30 hover:opacity-70 cursor-pointer transition-opacity"
          style={{ color: txtSecondary }}
          onClick={() => setEditing(true)}
        />
      )}
    </div>
  );
};
/* ── Bio Section with Inline Edit ── */
const BioSection = ({ bio, setBio, cardBg, cardBorder, txtBody, txtSecondary, accentColor }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(bio || '');
  const textAreaRef = useRef(null);
  useEffect(() => { setDraft(bio || ''); }, [bio]);
  useEffect(() => { if (editing && textAreaRef.current) textAreaRef.current.focus(); }, [editing]);
  const commit = () => {
    const count = draft.trim() ? draft.trim().split(/\s+/).length : 0;
    if (count > 120) {
      toast.error('Bio cannot exceed 120 words');
      return;
    }
    setEditing(false);
    if (draft !== bio) setBio(draft);
  };
  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;
  return (
    <section className="rounded-2xl p-6 mb-6 transition-all" style={{ background: cardBg, border: cardBorder }}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold" style={{ color: txtBody }}>About</h2>
        {editing && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${wordCount > 120 ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
            {wordCount} / 120 words
          </span>
        )}
      </div>
      {editing ? (
        <div className="rounded-xl p-4 transition-all" style={{ background: 'rgba(37,99,235,0.04)', border: `1.5px solid ${wordCount > 120 ? '#ef4444' : accentColor}` }}>
          <textarea
            ref={textAreaRef}
            className="w-full bg-transparent border-none focus:outline-none resize-none text-sm leading-relaxed"
            style={{ color: txtBody, minHeight: '80px' }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={(e) => {
              // Only commit if clicked outside the entire bio section or on the button
              // For simplicity, we'll keep the manual "Done" button as the main way
            }}
            placeholder="Tell us about yourself..."
          />
          <div className="flex justify-end mt-2">
            <button
              onMouseDown={commit}
              className="text-xs font-semibold px-4 py-2 rounded-lg text-white shadow-sm transition-all"
              style={{ background: wordCount > 120 ? '#ef4444' : accentColor }}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div onClick={() => setEditing(true)} className="cursor-pointer group relative">
          <Pencil className="absolute -right-1 -top-1 w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition-opacity" style={{ color: txtSecondary }} />
          {bio ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap transition-colors" style={{ color: txtBody }}>
              {bio}
            </p>
          ) : (
            <p className="text-sm italic" style={{ color: txtSecondary }}>Add a short bio to introduce yourself...</p>
          )}
        </div>
      )}
    </section>
  );
};
const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, openLogin, loading: authLoading } = useAuth();
  const returnTo = location.state?.returnTo;
  const returnState = location.state?.returnState;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [bio, setBio] = useState('');
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [openToRoles, setOpenToRoles] = useState([]);
  const [roleInput, setRoleInput] = useState('');
  const [resumeFileName, setResumeFileName] = useState(null);
  const [removingResume, setRemovingResume] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [companySuggestionsLoading, setCompanySuggestionsLoading] = useState(false);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(null);
  const [collegeSuggestions, setCollegeSuggestions] = useState([]);
  const [collegeSuggestionsLoading, setCollegeSuggestionsLoading] = useState(false);
  const [activeCollegeIndex, setActiveCollegeIndex] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  // Section edit state: only one section can be in edit mode at a time
  const [editingSection, setEditingSection] = useState(null); // 'experience' | 'education' | null
  // For per-item editing within a section
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  // Draft for editing single items
  const [editDraft, setEditDraft] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const bypassUnsavedGuardRef = useRef(false);
  const hydrateFromProfile = useCallback((profile) => {
    if (!profile) return;
    setName(profile.name || '');
    setEmail(profile.email || user?.email || '');
    setPhoneNumber(profile.phone_number || '');
    setLinkedinUrl(profile.linkedin_url || '');
    setBio(profile.bio || '');
    setExperiences(
      profile.work_experiences && profile.work_experiences.length
        ? profile.work_experiences.map((exp) => ({
          id: exp.id,
          company: exp.company || '',
          role: exp.role || '',
          start_date: toMonthValue(exp.start_date),
          end_date: toMonthValue(exp.end_date),
          currently_working: !exp.end_date,
        })).sort((a, b) => {
          const scoreA = a.currently_working ? '9999-99' : (a.end_date || a.start_date || '');
          const scoreB = b.currently_working ? '9999-99' : (b.end_date || b.start_date || '');
          if (scoreA === scoreB) return (b.start_date || '').localeCompare(a.start_date || '');
          return scoreB.localeCompare(scoreA);
        })
        : []
    );
    setEducations(
      profile.educations && profile.educations.length
        ? profile.educations.map((edu) => ({
          id: edu.id,
          college: edu.college || '',
          degree: edu.degree || '',
          start_date: toMonthValue(edu.start_date),
          end_date: toMonthValue(edu.end_date),
          currently_studying: !edu.end_date,
        })).sort((a, b) => {
          const scoreA = a.currently_studying ? '9999-99' : (a.end_date || a.start_date || '');
          const scoreB = b.currently_studying ? '9999-99' : (b.end_date || b.start_date || '');
          if (scoreA === scoreB) return (b.start_date || '').localeCompare(a.start_date || '');
          return scoreB.localeCompare(scoreA);
        })
        : []
    );
    if (Array.isArray(profile.skills)) {
      setSkills(profile.skills.map(s => typeof s === 'string' ? { skill: s } : { skill: s.skill }));
    } else {
      setSkills([]);
    }
    if (Array.isArray(profile.open_to_roles)) {
      setOpenToRoles(profile.open_to_roles.map(r => typeof r === 'string' ? r : (r.role || '')));
    } else {
      setOpenToRoles([]);
    }
    setResumeFileName(profile.profile_id || null);
    setSavedSnapshot(normalizeComparableProfile(profile));
  }, [user?.email]);
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      openLogin();
      navigate('/');
      return;
    }
    setLoading(true);
    getProfile()
      .then((data) => {
        hydrateFromProfile(data);
      })
      .catch((err) => {
        console.error('Failed to load profile', err);
        setEmail(user.email);
        setName(user.name || user.email?.split('@')[0] || '');
      })
      .finally(() => setLoading(false));
  }, [authLoading, user, openLogin, navigate, hydrateFromProfile]);
  useEffect(() => {
    if (activeCompanyIndex == null) {
      setCompanySuggestions([]);
      setCompanySuggestionsLoading(false);
      return;
    }
    const draftCompany = editDraft?.company || '';
    const q = draftCompany.trim();
    if (q.length < 2) {
      setCompanySuggestions([]);
      setCompanySuggestionsLoading(false);
      return;
    }
    const t = setTimeout(() => {
      setCompanySuggestionsLoading(true);
      searchOrganizations(q)
        .then((list) => setCompanySuggestions(Array.isArray(list) ? list : []))
        .catch(() => setCompanySuggestions([]))
        .finally(() => setCompanySuggestionsLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [activeCompanyIndex, editDraft?.company]);
  useEffect(() => {
    if (activeCollegeIndex == null) {
      setCollegeSuggestions([]);
      setCollegeSuggestionsLoading(false);
      return;
    }
    const draftCollege = editDraft?.college || '';
    const q = draftCollege.trim();
    if (q.length < 2) {
      setCollegeSuggestions([]);
      setCollegeSuggestionsLoading(false);
      return;
    }
    const t = setTimeout(() => {
      setCollegeSuggestionsLoading(true);
      searchSchools(q)
        .then((list) => setCollegeSuggestions(Array.isArray(list) ? list : []))
        .catch(() => setCollegeSuggestions([]))
        .finally(() => setCollegeSuggestionsLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [activeCollegeIndex, editDraft?.college]);
  const handleAddSkill = () => {
    const value = (skillInput || '').trim();
    if (!value) return;
    if (skills.some(s => s.skill.toLowerCase() === value.toLowerCase())) {
      setSkillInput('');
      return;
    }
    if (skills.length >= 25) {
      toast.error('You can add up to 25 key skills');
      return;
    }
    setSkills([...skills, { skill: value }]);
    setSkillInput('');
  };
  const handleSkillKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    } else if (e.key === 'Backspace' && !skillInput && skills.length) {
      e.preventDefault();
      setSkills(skills.slice(0, -1));
    }
  };
  const handleRemoveSkill = (skillName) => {
    setSkills(skills.filter((s) => s.skill !== skillName));
  };
  /* ── role handlers ── */
  const handleAddRole = () => {
    const v = (roleInput || '').trim();
    if (!v) return;
    if (openToRoles.some(r => r.toLowerCase() === v.toLowerCase())) { setRoleInput(''); return; }
    if (openToRoles.length >= 7) { toast.error('You can add up to 7 roles'); return; }
    setOpenToRoles([...openToRoles, v]);
    setRoleInput('');
  };
  const handleRoleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddRole(); }
    else if (e.key === 'Backspace' && !roleInput && openToRoles.length) {
      e.preventDefault();
      setOpenToRoles(openToRoles.slice(0, -1));
    }
  };
  const handleRemoveRole = (rName) => setOpenToRoles(openToRoles.filter((r) => r !== rName));
  /* ── Section edit helpers ── */
  const openEditSection = (section) => {
    setEditingSection(section);
    setEditingItemIndex(null);
    setEditDraft(null);
  };
  const closeEditSection = () => {
    // Remove any blank entries before closing
    setExperiences(prev => prev.filter(e => !isBlankExperience(e)));
    setEducations(prev => prev.filter(e => !isBlankEducation(e)));
    setEditingSection(null);
    setEditingItemIndex(null);
    setEditDraft(null);
    setActiveCompanyIndex(null);
    setActiveCollegeIndex(null);
  };
  const handleSectionDone = () => {
    closeEditSection();
  };
  const startEditItem = (section, index) => {
    if (section === 'experience') {
      setEditDraft({ ...experiences[index] });
    } else if (section === 'education') {
      setEditDraft({ ...educations[index] });
    }
    setEditingItemIndex(index);
  };
  const startAddItem = (section) => {
    // Guard: don't add if there's already a blank entry being edited
    if (section === 'experience') {
      const hasBlank = experiences.some(isBlankExperience);
      if (hasBlank) {
        toast.info('Please fill in the existing experience first');
        return;
      }
    } else if (section === 'education') {
      const hasBlank = educations.some(isBlankEducation);
      if (hasBlank) {
        toast.info('Please fill in the existing education first');
        return;
      }
    }
    openEditSection(section);
    if (section === 'experience') {
      const newItems = [...experiences, emptyExperience()];
      setExperiences(newItems);
      setEditDraft(emptyExperience());
      setEditingItemIndex(newItems.length - 1);
    } else if (section === 'education') {
      const newItems = [...educations, emptyEducation()];
      setEducations(newItems);
      setEditDraft(emptyEducation());
      setEditingItemIndex(newItems.length - 1);
    }
  };
  const cancelEditItem = (section) => {
    if (editDraft && editingItemIndex != null) {
      // If the item was newly added and blank, remove it
      if (section === 'experience') {
        const item = experiences[editingItemIndex];
        if (isBlankExperience(item)) {
          setExperiences(experiences.filter((_, i) => i !== editingItemIndex));
        }
      } else if (section === 'education') {
        const item = educations[editingItemIndex];
        if (isBlankEducation(item)) {
          setEducations(educations.filter((_, i) => i !== editingItemIndex));
        }
      }
    }
    setEditingItemIndex(null);
    setEditDraft(null);
    setActiveCompanyIndex(null);
    setActiveCollegeIndex(null);
  };
  const saveEditItem = (section) => {
    if (!editDraft || editingItemIndex == null) return;
    if (section === 'experience') {
      const original = experiences[editingItemIndex];
      const norm = (item) => ({
        c: (item.company || '').trim(),
        r: (item.role || '').trim(),
        s: item.start_date || '',
        e: item.currently_working ? '' : (item.end_date || ''),
        cw: !!item.currently_working
      });
      const hasChanged = JSON.stringify(norm(original)) !== JSON.stringify(norm(editDraft));
      const finalItem = hasChanged ? { ...editDraft, id: undefined } : { ...editDraft };
      setExperiences(prev => prev.map((exp, i) => i === editingItemIndex ? finalItem : exp));
    } else if (section === 'education') {
      const start = editDraft.start_date || '';
      const end = editDraft.end_date || '';
      if (start && (!isValidMonth(start) || start < MIN_MONTH || start > CURRENT_YEAR_END_MONTH)) {
        toast.error(`Start date must be between ${MIN_MONTH} and ${CURRENT_YEAR_END_MONTH}.`);
        return;
      }
      if (end && (!isValidMonth(end) || end < MIN_MONTH || end > CURRENT_YEAR_END_MONTH)) {
        toast.error(`End date cannot be after ${CURRENT_YEAR_END_MONTH}.`);
        return;
      }
      if (!editDraft.currently_studying && start && end && end < start) {
        toast.error('End date cannot be before start date.');
        return;
      }
      const original = educations[editingItemIndex];
      const norm = (item) => ({
        c: (item.college || '').trim(),
        d: (item.degree || '').trim(),
        s: item.start_date || '',
        e: item.currently_studying ? '' : (item.end_date || ''),
        cs: !!item.currently_studying
      });
      const hasChanged = JSON.stringify(norm(original)) !== JSON.stringify(norm(editDraft));
      const finalItem = hasChanged ? { ...editDraft, id: undefined } : { ...editDraft };
      setEducations(prev => prev.map((edu, i) => i === editingItemIndex ? finalItem : edu));
    }
    // Done should finish this block's edit mode only.
    closeEditSection();
  };
  const handleRemoveExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
      setEditDraft(null);
    } else if (editingItemIndex != null && editingItemIndex > index) {
      setEditingItemIndex(editingItemIndex - 1);
    }
  };
  const handleRemoveEducation = (index) => {
    setEducations(educations.filter((_, i) => i !== index));
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
      setEditDraft(null);
    } else if (editingItemIndex != null && editingItemIndex > index) {
      setEditingItemIndex(editingItemIndex - 1);
    }
  };
  const handleEditDraftChange = (field, value) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      if (field === 'currently_working') {
        return { ...prev, currently_working: !!value, end_date: value ? '' : prev.end_date };
      }
      if (field === 'currently_studying') {
        return { ...prev, currently_studying: !!value, end_date: value ? '' : prev.end_date };
      }
      if (field === 'end_date') {
        const nextEnd = clampToCurrentYearEnd(value);
        if (prev.start_date && nextEnd && nextEnd < prev.start_date) {
          toast.error('End date cannot be before start date');
          return prev;
        }
        return { ...prev, end_date: nextEnd, currently_working: nextEnd ? false : prev.currently_working, currently_studying: nextEnd ? false : prev.currently_studying };
      }
      if (field === 'start_date') {
        const nextStart = clampToCurrentYearEnd(value);
        if (prev.end_date && nextStart && prev.end_date < nextStart) {
          return { ...prev, start_date: nextStart, end_date: '' };
        }
        return { ...prev, start_date: nextStart };
      }
      return { ...prev, [field]: value };
    });
  };
  const handleSave = async (e) => {
    e?.preventDefault();
    if (!user) {
      openLogin();
      return;
    }
    setSaving(true);
    try {
      const experienceDateError = validateProfileDates(experiences, 'Experience', 'currently_working');
      if (experienceDateError) {
        toast.error(experienceDateError);
        return;
      }
      const educationDateError = validateProfileDates(educations, 'Education', 'currently_studying');
      if (educationDateError) {
        toast.error(educationDateError);
        return;
      }
      const payload = {
        name: name || user.name || '',
        phone_number: phoneNumber || '',
        linkedin_url: linkedinUrl || '',
        bio: bio || '',
        work_experiences: experiences
          .map((exp) => ({
            id: exp.id,
            company: exp.company || '',
            role: exp.role || '',
            start_date: exp.start_date || '',
            end_date: exp.currently_working ? '' : (exp.end_date || ''),
          }))
          .filter((exp) => exp.company || exp.role || exp.start_date || exp.end_date),
        educations: educations
          .map((edu) => ({
            id: edu.id,
            college: edu.college || '',
            degree: edu.degree || '',
            start_date: edu.start_date || '',
            end_date: edu.currently_studying ? '' : (edu.end_date || ''),
          }))
          .filter((edu) => edu.college || edu.degree || edu.start_date || edu.end_date),
        skills,
        open_to_roles: openToRoles,
      };
      const updated = await updateProfile(payload);
      hydrateFromProfile(updated);
      clearInsightsCache();
      toast.success('Profile saved');
      if (returnTo) {
        navigate(returnTo, { state: { ...returnState, autoOpenReferral: true } });
      }
      return true;
    } catch (err) {
      console.error('Failed to save profile', err);
      toast.error(err.message || 'Failed to save profile');
      return false;
    } finally {
      setSaving(false);
    }
  };
  const handleResumeUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF resume');
      return;
    }
    if (file.size > MAX_RESUME_SIZE_BYTES) {
      toast.error('Resume must be 300 KB or smaller');
      event.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const updated = await uploadResume(file);
      hydrateFromProfile(updated);
      clearInsightsCache();
      toast.success('Resume uploaded and profile auto-filled');
    } catch (err) {
      console.error('Failed to upload resume', err);
      toast.error(err.message || 'Failed to upload resume');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };
  const handleRemoveResume = async () => {
    if (!resumeFileName) return;
    setRemovingResume(true);
    try {
      const updated = await deleteResume();
      hydrateFromProfile(updated);
      setResumeFileName(null);
      clearInsightsCache();
      toast.success('Resume removed');
    } catch (err) {
      console.error('Failed to remove resume', err);
      toast.error(err.message || 'Failed to remove resume');
    } finally {
      setRemovingResume(false);
    }
  };
  const handleDownloadResume = () => {
    if (!resumeFileName) return;
    downloadResume(resumeFileName).catch((err) => {
      console.error('Failed to download resume', err);
      toast.error(err.message || 'Failed to download resume');
    });
  };
  const { isDark } = useTheme();
  const appCls = isDark ? 'dark-app' : 'light-app';
  const buildCurrentSnapshot = useCallback(() => {
    let currentExps = experiences;
    if (editingSection === 'experience' && editingItemIndex != null && editDraft) {
      currentExps = experiences.map((exp, i) => i === editingItemIndex ? editDraft : exp);
    }
    let currentEdus = educations;
    if (editingSection === 'education' && editingItemIndex != null && editDraft) {
      currentEdus = educations.map((edu, i) => i === editingItemIndex ? editDraft : edu);
    }
    return normalizeComparableProfile({
      name: name || user?.name || '',
      phone_number: phoneNumber || '',
      linkedin_url: linkedinUrl || '',
      bio: bio || '',
      work_experiences: currentExps,
      educations: currentEdus,
      skills,
      open_to_roles: openToRoles,
    });
  }, [name, phoneNumber, linkedinUrl, bio, experiences, educations, skills, openToRoles, user?.name, editingSection, editingItemIndex, editDraft]);
  const hasUnsavedChanges = (
    JSON.stringify(buildCurrentSnapshot()) !== JSON.stringify(savedSnapshot || normalizeComparableProfile({}))
  );
  const requestLeaveConfirmation = useCallback((action) => {
    if (!action) return;
    setPendingLeaveAction(() => action);
    setShowLeaveModal(true);
  }, []);
  const runLeaveAction = useCallback(() => {
    if (!pendingLeaveAction) return;
    const action = pendingLeaveAction;
    setPendingLeaveAction(null);
    setShowLeaveModal(false);
    bypassUnsavedGuardRef.current = true;
    try {
      action();
    } finally {
      setTimeout(() => {
        bypassUnsavedGuardRef.current = false;
      }, 0);
    }
  }, [pendingLeaveAction]);
  const handleAttemptLeave = useCallback((action) => {
    if (bypassUnsavedGuardRef.current || !hasUnsavedChanges) {
      action();
      return;
    }
    requestLeaveConfirmation(action);
  }, [hasUnsavedChanges, requestLeaveConfirmation]);
  const handleSaveAndLeave = async () => {
    const didSave = await handleSave();
    if (didSave) runLeaveAction();
  };
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);
  // ── Color palette: white/blue in light mode, matches rest of the app ──
  const bgPage = isDark ? '#0d1019' : '#eef2fb';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const txtBody = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';
  const txtDim = isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const txtSecondary = isDark ? 'rgba(255,255,255,0.6)' : '#64748b';
  const borderSub = isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0';
  const accentColor = isDark ? '#F59E0B' : '#2563eb';
  const accentBg = isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : '#2563eb';
  const accentTxt = isDark ? '#1C1814' : '#ffffff';
  // Tags - matching user image (indigo for skills, sky for roles)
  const skillTagBg = isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff';
  const skillTagBorder = isDark ? '1px solid rgba(99,102,241,0.2)' : '1px solid #e0e7ff';
  const skillTagText = isDark ? '#818cf8' : '#4f46e5';
  const roleTagBg = isDark ? 'rgba(14,165,233,0.1)' : '#f0f9ff';
  const roleTagBorder = isDark ? '1px solid rgba(14,165,233,0.2)' : '1px solid #e0f2fe';
  const roleTagText = isDark ? '#38bdf8' : '#0284c7';
  const tagXColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)';
  const emptyText = isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8';
  const softText = isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8';
  const hintText = isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const inputBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';
  const btnSolidBg = isDark ? '#F59E0B' : '#2563eb';
  const btnSolidText = isDark ? '#1C1814' : '#ffffff';
  const iconBtnColor = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const iconBtnHover = isDark ? 'rgba(255,255,255,0.8)' : '#2563eb';
  const addBtnBg = isDark ? 'rgba(59,130,246,0.08)' : 'rgba(37,99,235,0.05)';
  const addBtnBorder = isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid rgba(37,99,235,0.15)';
  const addBtnText = isDark ? '#60a5fa' : '#2563eb';
  if (authLoading || loading) {
    return (
      <div className={`${appCls} flex items-center justify-center`} style={{ minHeight: '100vh', background: bgPage }}>
        <div className="flex items-center gap-3">
          <svg className="animate-spin h-6 w-6" style={{ color: accentColor }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <span className="text-sm font-medium" style={{ color: txtBody }}>Loading profile...</span>
        </div>
      </div>
    );
  }
  const inputStyle = { background: inputBg, border: inputBorder, color: txtBody };
  return (
    <div className={appCls} style={{ background: bgPage, minHeight: '100vh', paddingTop: 56 }}>
      {/* ── Header ── */}
      <header className="profile-app-header z-50" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 56, background: bgPage, borderBottom: `1px solid ${borderSub}`, backdropFilter: 'blur(16px)' }}>
        <div className="profile-app-header-inner flex items-center justify-between gap-3" style={{ height: 56, padding: '0 16px 0 0' }}>
          <div className="profile-app-header-left flex items-center gap-[14px]">
            <button
              onClick={() => handleAttemptLeave(() => navigate(-1))}
              className="profile-app-back flex items-center gap-1.5 transition-all"
              style={{ color: txtSecondary, height: 56, padding: '0 16px', border: 'none', background: 'transparent', fontSize: 14, fontWeight: 500 }}
              onMouseEnter={e => { e.currentTarget.style.color = txtBody; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = txtSecondary; e.currentTarget.style.background = 'transparent'; }}
            >
              <ArrowLeft size={15} />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="h-6 w-px" style={{ background: borderSub }} />
            <div className="profile-app-logo flex items-center gap-2 cursor-pointer" onClick={() => handleAttemptLeave(() => navigate('/'))}>
              <div className="flex items-center justify-center bg-white rounded-md p-1" style={{ width: 32, height: 32, border: `1px solid ${borderSub}` }}>
                <img src="/assets/logo.png" alt="fairhire logo" className="w-full h-full object-contain" />
              </div>
              <span className="profile-app-brand font-bold" style={{ fontSize: 16, fontFamily: "'Playfair Display', serif", color: txtBody }}>
                fairhire
              </span>
            </div>
          </div>
          <div className="profile-app-header-actions flex items-center gap-3">
            <ThemeToggle />
            <div className="relative">
              {user ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                    style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.08)' }}
                  >
                    <UserIcon className="w-5 h-5" style={{ color: txtSecondary }} />
                  </button>
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 rounded-xl shadow-xl p-2 z-40" style={{ background: isDark ? '#1a1e2e' : '#ffffff', border: cardBorder }}>
                      <div className="px-3 py-2 mb-1" style={{ borderBottom: `1px solid ${borderSub}` }}>
                        <p className="text-xs" style={{ color: txtDim }}>Signed in as</p>
                        <p className="text-sm font-semibold truncate" style={{ color: txtBody }}>{user.name}</p>
                      </div>
                      <button
                        onClick={() => { setIsProfileMenuOpen(false); handleAttemptLeave(() => navigate('/profile')); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all"
                        style={{ color: txtSecondary }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <UserIcon className="w-4 h-4" /> Profile
                      </button>
                      <button
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleAttemptLeave(() => {
                            logout();
                            navigate('/');
                          });
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all"
                        style={{ color: '#F87171' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <LogOut className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => openLogin()} className="px-4 sm:px-6 py-2 rounded-full text-sm font-semibold transition-all" style={{ background: accentBg, color: accentTxt }}>
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* ── Main ── */}
      <main className="profile-main max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* ── Hero Card: Avatar, Name, Email, Phone, LinkedIn, Resume ── */}
        <section className="rounded-2xl mb-6 overflow-hidden" style={{ background: cardBg, border: cardBorder }}>
          {/* Top accent bar — flush to card top */}
          <div style={{ width: '100%', height: '4px', background: isDark ? '#F59E0B' : '#2563eb' }} />
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Left: Avatar + info */}
              <div className="flex-1">
                <div className="flex items-start gap-5">
                  {/* Avatar — first letter of name */}
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden select-none" style={{ background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(37,99,235,0.1)', border: `2px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.2)'}` }}>
                      <span className="text-3xl font-bold" style={{ color: accentColor }}>
                        {(name || email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {/* Name + Email + Fields */}
                  <div className="flex-1 min-w-0">
                    {/* Name — inline edit with pen icon that opens boxed edit */}
                    <div className="flex items-center gap-2 mb-1">
                      <NameEditField
                        name={name}
                        setName={setName}
                        txtBody={txtBody}
                        txtSecondary={txtSecondary}
                        accentColor={accentColor}
                      />
                    </div>
                    {/* Email */}
                    <p className="text-sm mb-4" style={{ color: txtSecondary }}>{email}</p>
                    {/* Phone + LinkedIn — with visible editable styling */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                      <InlineEditField
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        placeholder="Add phone number"
                        icon={Phone}
                        iconColor={isDark ? 'rgba(255,255,255,0.4)' : '#64748b'}
                        editingBorderColor={accentColor}
                        style={{ fontSize: '13px', color: phoneNumber ? txtBody : txtSecondary }}
                      />
                      <InlineEditField
                        value={linkedinUrl}
                        onChange={setLinkedinUrl}
                        placeholder="Add LinkedIn URL"
                        icon={Linkedin}
                        iconColor={isDark ? 'rgba(255,255,255,0.4)' : '#0A66C2'}
                        editingBorderColor={accentColor}
                        style={{ fontSize: '13px', color: linkedinUrl ? txtBody : txtSecondary }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Right: Resume — aligned to top, same height as name row */}
              <div className="flex-shrink-0 flex items-start">
                {resumeFileName ? (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: inputBg, border: inputBorder }}>
                    <FileText className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
                    <button type="button" onClick={handleDownloadResume} className="truncate max-w-[160px] font-medium transition-colors" style={{ color: txtBody }}>
                      {resumeFileName}
                    </button>
                    <button type="button" onClick={handleRemoveResume} disabled={removingResume} className="p-0.5 rounded transition-colors disabled:opacity-50" style={{ color: softText }} title="Remove resume">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all" style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.2)', color: isDark ? '#F59E0B' : '#2563eb' }}>
                    <Upload className="w-4 h-4" />
                    <span>{uploading ? 'Uploading...' : 'Upload Resume'}</span>
                    <input type="file" accept="application/pdf" className="hidden" onChange={handleResumeUpload} disabled={uploading} />
                  </label>
                )}
              </div>
            </div>
          </div>
        </section>
        {/* ── Bio Section ── */}
        <BioSection
          bio={bio}
          setBio={setBio}
          cardBg={cardBg}
          cardBorder={cardBorder}
          txtBody={txtBody}
          txtSecondary={txtSecondary}
          accentColor={accentColor}
        />
        {/* ── Experience & Education (Side by Side) ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {/* Experience Card */}
          <section className="rounded-2xl p-6" style={{ background: cardBg, border: cardBorder }}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold" style={{ color: txtBody }}>Experience</h2>
              <div className="flex items-center gap-1">
                {experiences.length > 0 && editingSection !== 'experience' && (
                  <button
                    type="button"
                    onClick={() => openEditSection('experience')}
                    className="p-1.5 rounded-lg transition-all"
                    style={{ color: iconBtnColor }}
                    onMouseEnter={e => { e.currentTarget.style.color = iconBtnHover; }}
                    onMouseLeave={e => { e.currentTarget.style.color = iconBtnColor; }}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startAddItem('experience')}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ color: iconBtnColor }}
                  onMouseEnter={e => { e.currentTarget.style.color = iconBtnHover; }}
                  onMouseLeave={e => { e.currentTarget.style.color = iconBtnColor; }}
                  title="Add"
                >
                  <Plus className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>
            <div
              className="mt-3 mb-2 rounded-lg px-3 py-2 text-xs"
              style={{
                color: isDark ? '#93c5fd' : '#1d4ed8',
                background: isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe',
                borderLeft: `4px solid ${isDark ? '#60a5fa' : '#3b82f6'}`
              }}
            >
              Enter company names exactly as on LinkedIn for accurate matches.
            </div>
            {experiences.length === 0 && editingSection !== 'experience' ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Briefcase className="w-10 h-10 mb-3" style={{ color: emptyText }} />
                <p className="text-sm mb-1" style={{ color: emptyText }}>No experience added yet</p>
                <p className="text-xs mb-4" style={{ color: emptyText }}>Add your work history to get better insights</p>
                <button
                  type="button"
                  onClick={() => startAddItem('experience')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all"
                  style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.2)', color: isDark ? '#F59E0B' : '#2563eb' }}
                >
                  <Plus className="w-3.5 h-3.5" /> Add Experience
                </button>
              </div>
            ) : (
              /* Experience list */
              <div className="space-y-0">
                {experiences.map((exp, index) => (
                  <div key={index}>
                    {/* View mode item */}
                    <div className="flex items-start gap-3 py-3" style={{ borderBottom: index < experiences.length - 1 ? `1px solid ${borderSub}` : 'none' }}>
                      <div className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: isDark ? 'rgba(255,255,255,0.3)' : accentColor }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: txtBody }}>{exp.company || 'Untitled'}</p>
                        <p className="text-xs" style={{ color: txtSecondary }}>{exp.role || ''}</p>
                        <p className="text-xs" style={{ color: softText }}>
                          {formatMonthYear(exp.start_date)}{exp.start_date && (exp.end_date || exp.currently_working) ? ' — ' : ''}{exp.currently_working ? 'Present' : formatMonthYear(exp.end_date)}
                        </p>
                      </div>
                    </div>
                    {/* Edit/Remove buttons when section is in edit mode */}
                    {editingSection === 'experience' && editingItemIndex !== index && (
                      <div className="flex items-center gap-2 pb-3 ml-5">
                        <button
                          type="button"
                          onClick={() => startEditItem('experience', index)}
                          className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                          style={{ border: isDark ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(37,99,235,0.25)', color: isDark ? '#F59E0B' : '#2563eb', background: 'transparent' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveExperience(index)}
                          className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                          style={{ color: txtSecondary, background: 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#F87171'}
                          onMouseLeave={e => e.currentTarget.style.color = txtSecondary}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {/* Inline edit form */}
                    {editingSection === 'experience' && editingItemIndex === index && editDraft && (/* eslint-disable-line */
                      <div className="rounded-xl p-4 mb-3 mt-1" style={{ background: inputBg, border: inputBorder }}>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: hintText }}>EDIT EXPERIENCE</p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>COMPANY</label>
                            <div className="relative">
                              <input
                                type="text"
                                value={editDraft.company}
                                onChange={e => { handleEditDraftChange('company', e.target.value); }}
                                onFocus={() => setActiveCompanyIndex(index)}
                                onBlur={() => setTimeout(() => setActiveCompanyIndex(c => c === index ? null : c), 160)}
                                placeholder="Company"
                                className="w-full h-9 px-3 rounded-lg text-sm focus:outline-none"
                                style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                              />
                              {activeCompanyIndex === index && (companySuggestionsLoading || companySuggestions.length > 0) && (
                                <div className="absolute left-0 right-0 top-full mt-1 rounded-xl shadow-xl z-30 max-h-44 overflow-auto"
                                  style={{ background: isDark ? '#1a1e2e' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }}
                                >
                                  {companySuggestionsLoading && <div className="px-3 py-2 text-xs" style={{ color: txtDim }}>Searching...</div>}
                                  {!companySuggestionsLoading && companySuggestions.map(org => (
                                    <button key={org.id || org.name} type="button"
                                      onMouseDown={e => { e.preventDefault(); handleEditDraftChange('company', org.name || ''); setActiveCompanyIndex(null); }}
                                      className="w-full text-left px-3 py-2 text-sm transition-colors"
                                      style={{ color: txtBody }}
                                      onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >{org.name}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>ROLE</label>
                            <input
                              type="text"
                              value={editDraft.role}
                              onChange={e => handleEditDraftChange('role', e.target.value)}
                              placeholder="Role"
                              className="w-full h-9 px-3 rounded-lg text-sm focus:outline-none"
                              style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>START</label>
                            <MonthYearPicker
                              value={editDraft.start_date || ''}
                              onChange={(v) => handleEditDraftChange('start_date', v)}
                              className="w-full h-9 px-3 rounded-lg text-sm"
                              style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                              maxMonth={CURRENT_YEAR_END_MONTH}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>END</label>
                            <MonthYearPicker
                              value={editDraft.end_date || ''}
                              onChange={(v) => handleEditDraftChange('end_date', v)}
                              disabled={!!editDraft.currently_working}
                              className="w-full h-9 px-3 rounded-lg text-sm"
                              maxMonth={CURRENT_YEAR_END_MONTH}
                              style={editDraft.currently_working
                                ? { background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`, color: txtBody, opacity: 0.35, cursor: 'not-allowed' }
                                : { background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }
                              }
                            />
                          </div>
                        </div>
                        {/* Currently working toggle */}
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer mb-4" style={{ color: softText }}>
                          <div
                            className="relative w-9 h-5 rounded-full transition-colors cursor-pointer"
                            style={{ background: editDraft.currently_working ? accentColor : (isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1') }}
                            onClick={() => handleEditDraftChange('currently_working', !editDraft.currently_working)}
                          >
                            <div
                              className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                              style={{
                                background: '#fff',
                                left: editDraft.currently_working ? '18px' : '2px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            />
                          </div>
                          Currently working here
                        </label>
                        <div className="flex items-center gap-3 justify-end">
                          <button type="button" onClick={() => cancelEditItem('experience')} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ color: txtSecondary }}>Cancel</button>
                          <button type="button" onClick={() => saveEditItem('experience')} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ background: btnSolidBg, color: btnSolidText }}>Done</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Close edit mode button */}
                {editingSection === 'experience' && editingItemIndex === null && (
                  <div className="flex justify-end pt-3">
                    <button type="button" onClick={handleSectionDone} className="text-xs font-medium px-3 py-1.5 rounded-lg transition-all" style={{ color: isDark ? '#F59E0B' : '#2563eb', background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.2)' }}>
                      Done
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
          {/* Education Card */}
          <section className="rounded-2xl p-6" style={{ background: cardBg, border: cardBorder }}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold" style={{ color: txtBody }}>Education</h2>
              <div className="flex items-center gap-1">
                {educations.length > 0 && editingSection !== 'education' && (
                  <button
                    type="button"
                    onClick={() => openEditSection('education')}
                    className="p-1.5 rounded-lg transition-all"
                    style={{ color: iconBtnColor }}
                    onMouseEnter={e => { e.currentTarget.style.color = iconBtnHover; }}
                    onMouseLeave={e => { e.currentTarget.style.color = iconBtnColor; }}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startAddItem('education')}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ color: iconBtnColor }}
                  onMouseEnter={e => { e.currentTarget.style.color = iconBtnHover; }}
                  onMouseLeave={e => { e.currentTarget.style.color = iconBtnColor; }}
                  title="Add"
                >
                  <Plus className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>
            <div
              className="mt-3 mb-2 rounded-lg px-3 py-2 text-xs"
              style={{
                color: isDark ? '#93c5fd' : '#1d4ed8',
                background: isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe',
                borderLeft: `4px solid ${isDark ? '#60a5fa' : '#3b82f6'}`
              }}
            >
              Enter institution names exactly as on LinkedIn for accurate matches.
            </div>
            {educations.length === 0 && editingSection !== 'education' ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <GraduationCap className="w-10 h-10 mb-3" style={{ color: emptyText }} />
                <p className="text-sm mb-1" style={{ color: emptyText }}>No education added yet</p>
                <p className="text-xs mb-4" style={{ color: emptyText }}>Add your educational background</p>
                <button
                  type="button"
                  onClick={() => startAddItem('education')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all"
                  style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.2)', color: isDark ? '#F59E0B' : '#2563eb' }}
                >
                  <Plus className="w-3.5 h-3.5" /> Add Education
                </button>
              </div>
            ) : (
              /* Education list */
              <div className="space-y-0">
                {educations.map((edu, index) => (
                  <div key={index}>
                    {/* View mode item */}
                    <div className="flex items-start gap-3 py-3" style={{ borderBottom: index < educations.length - 1 ? `1px solid ${borderSub}` : 'none' }}>
                      <div className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: isDark ? 'rgba(255,255,255,0.3)' : accentColor }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: txtBody }}>{edu.college || 'Untitled'}</p>
                        <p className="text-xs" style={{ color: txtSecondary }}>{edu.degree || ''}</p>
                        <p className="text-xs" style={{ color: softText }}>
                          {formatMonthYear(edu.start_date)}{edu.start_date && (edu.end_date || edu.currently_studying) ? ' — ' : ''}{edu.currently_studying ? 'Present' : formatMonthYear(edu.end_date)}
                        </p>
                      </div>
                    </div>
                    {/* Edit/Remove buttons */}
                    {editingSection === 'education' && editingItemIndex !== index && (
                      <div className="flex items-center gap-2 pb-3 ml-5">
                        <button
                          type="button"
                          onClick={() => startEditItem('education', index)}
                          className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                          style={{ border: isDark ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(37,99,235,0.25)', color: isDark ? '#F59E0B' : '#2563eb', background: 'transparent' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveEducation(index)}
                          className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                          style={{ color: txtSecondary, background: 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#F87171'}
                          onMouseLeave={e => e.currentTarget.style.color = txtSecondary}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {/* Inline edit form */}
                    {editingSection === 'education' && editingItemIndex === index && editDraft && (
                      <div className="rounded-xl p-4 mb-3 mt-1" style={{ background: inputBg, border: inputBorder }}>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: hintText }}>EDIT EDUCATION</p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>COLLEGE</label>
                            <div className="relative">
                              <input
                                type="text"
                                value={editDraft.college}
                                onChange={e => handleEditDraftChange('college', e.target.value)}
                                onFocus={() => setActiveCollegeIndex(index)}
                                onBlur={() => setTimeout(() => setActiveCollegeIndex(c => c === index ? null : c), 160)}
                                placeholder="College name"
                                className="w-full h-9 px-3 rounded-lg text-sm focus:outline-none"
                                style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                              />
                              {activeCollegeIndex === index && (collegeSuggestionsLoading || collegeSuggestions.length > 0) && (
                                <div className="absolute left-0 right-0 top-full mt-1 rounded-xl shadow-xl z-30 max-h-44 overflow-auto"
                                  style={{ background: isDark ? '#1a1e2e' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }}
                                >
                                  {collegeSuggestionsLoading && <div className="px-3 py-2 text-xs" style={{ color: txtDim }}>Searching...</div>}
                                  {!collegeSuggestionsLoading && collegeSuggestions.map(school => (
                                    <button key={school.id || school.name} type="button"
                                      onMouseDown={e => { e.preventDefault(); handleEditDraftChange('college', school.name || ''); setActiveCollegeIndex(null); }}
                                      className="w-full text-left px-3 py-2 text-sm transition-colors"
                                      style={{ color: txtBody }}
                                      onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >{school.name}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>DEGREE</label>
                            <input
                              type="text"
                              value={editDraft.degree}
                              onChange={e => handleEditDraftChange('degree', e.target.value)}
                              placeholder="Degree / Department"
                              className="w-full h-9 px-3 rounded-lg text-sm focus:outline-none"
                              style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>START</label>
                            <MonthYearPicker
                              value={editDraft.start_date || ''}
                              onChange={(v) => handleEditDraftChange('start_date', v)}
                              className="w-full h-9 px-3 rounded-lg text-sm"
                              style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }}
                              maxMonth={CURRENT_YEAR_END_MONTH}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: hintText }}>END</label>
                            <MonthYearPicker
                              value={editDraft.end_date || ''}
                              onChange={(v) => handleEditDraftChange('end_date', v)}
                              disabled={!!editDraft.currently_studying}
                              className="w-full h-9 px-3 rounded-lg text-sm"
                              maxMonth={CURRENT_YEAR_END_MONTH}
                              style={editDraft.currently_studying
                                ? { background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`, color: txtBody, opacity: 0.35, cursor: 'not-allowed' }
                                : { background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, color: txtBody }
                              }
                            />
                          </div>
                        </div>
                        {/* Currently studying toggle */}
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer mb-4" style={{ color: softText }}>
                          <div
                            className="relative w-9 h-5 rounded-full transition-colors cursor-pointer"
                            style={{ background: editDraft.currently_studying ? accentColor : (isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1') }}
                            onClick={() => handleEditDraftChange('currently_studying', !editDraft.currently_studying)}
                          >
                            <div
                              className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                              style={{
                                background: '#fff',
                                left: editDraft.currently_studying ? '18px' : '2px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            />
                          </div>
                          Currently studying here
                        </label>
                        <div className="flex items-center gap-3 justify-end">
                          <button type="button" onClick={() => cancelEditItem('education')} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ color: txtSecondary }}>Cancel</button>
                          <button type="button" onClick={() => saveEditItem('education')} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ background: btnSolidBg, color: btnSolidText }}>Done</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Close edit mode button */}
                {editingSection === 'education' && editingItemIndex === null && (
                  <div className="flex justify-end pt-3">
                    <button type="button" onClick={handleSectionDone} className="text-xs font-medium px-3 py-1.5 rounded-lg transition-all" style={{ color: isDark ? '#F59E0B' : '#2563eb', background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.2)' }}>
                      Done
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
        {/* ── Skills ── */}
        <section className="rounded-2xl p-6 sm:p-8 mb-6" style={{ background: cardBg, border: cardBorder }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ color: txtBody }}>Skills</h2>
            <span className="text-xs font-medium" style={{ color: softText }}>25 max</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4 min-h-[32px]">
            {skills.map(sk => (
              <span key={sk.skill} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: skillTagBg, border: skillTagBorder, color: skillTagText }}>
                {sk.skill}
                <button type="button" onClick={() => handleRemoveSkill(sk.skill)} style={{ color: skillTagText, opacity: 0.6 }}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {skills.length === 0 && (
              <span className="text-xs" style={{ color: emptyText }}>No skills added yet. Start typing below.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input type="text" value={skillInput}
              onChange={e => setSkillInput(e.target.value)}
              onKeyDown={handleSkillKeyDown}
              placeholder="Type a skill and press Enter"
              className="flex-1 h-10 px-3 rounded-lg text-sm focus:outline-none"
              style={inputStyle}
            />
            <button type="button" onClick={handleAddSkill}
              className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
              style={{ background: addBtnBg, border: addBtnBorder, color: addBtnText }}
            >
              Add
            </button>
          </div>
        </section>
        {/* ── Open to Roles ── */}
        <section className="rounded-2xl p-6 sm:p-8 mb-6" style={{ background: cardBg, border: cardBorder }}>
          <div className="mb-4">
            <h2 className="text-base font-semibold mb-1" style={{ color: txtBody }}>Open to Roles</h2>
            <p className="text-xs" style={{ color: softText }}>Suggested roles you are open to. Press Enter to add. Max 7 roles.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4 min-h-[32px]">
            {openToRoles.length === 0 ? (
              <span className="text-xs italic px-1" style={{ color: emptyText }}>No roles added yet.</span>
            ) : (
              openToRoles.map(role => (
                <span key={role} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: roleTagBg, border: roleTagBorder, color: roleTagText }}>
                  {role}
                  <button type="button" onClick={() => handleRemoveRole(role)} style={{ color: roleTagText, opacity: 0.6 }}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <input type="text" value={roleInput}
              onChange={e => setRoleInput(e.target.value)}
              onKeyDown={handleRoleKeyDown}
              placeholder="Type a role and press Enter"
              className="flex-1 h-10 px-3 rounded-lg text-sm focus:outline-none"
              style={inputStyle}
            />
            <button type="button" onClick={handleAddRole}
              className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
              style={{ background: addBtnBg, border: addBtnBorder, color: addBtnText }}
            >
              Add
            </button>
          </div>
        </section>
        {/* ── Save Button — bottom ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pb-8">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 rounded-xl text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm"
            style={{ background: accentBg, color: accentTxt, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </main>
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: cardBg, border: cardBorder }}
          >
            <h3 className="text-lg font-semibold mb-2" style={{ color: txtBody }}>Unsaved changes</h3>
            <p className="text-sm mb-5" style={{ color: txtSecondary }}>
              You have unsaved profile changes. Do you want to save them before leaving this page?
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowLeaveModal(false);
                  setPendingLeaveAction(null);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ color: txtSecondary }}
              >
                Stay
              </button>
              <button
                type="button"
                onClick={runLeaveAction}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ border: inputBorder, color: txtBody, background: inputBg }}
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSaveAndLeave}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                style={{ background: btnSolidBg, color: btnSolidText }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 640px) {
          .profile-app-header-inner {
            padding-right: 10px !important;
          }
          .profile-app-header-left {
            gap: 8px !important;
            min-width: 0;
          }
          .profile-app-back {
            padding: 0 10px !important;
          }
          .profile-app-logo {
            min-width: 0;
          }
          .profile-app-brand {
            display: none;
          }
          .profile-app-header-actions {
            gap: 8px !important;
          }
          .profile-main {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};
export default ProfilePage;
